import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageUploadField } from "@/components/admin/ImageUploadField";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function ProgramsContentManager() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content/programs").then((res) => setContent(res.data)); }, []);

  if (!content) return <p className="text-muted-foreground text-sm">Loading…</p>;

  const updateProgram = (i, field, value) => {
    const programs = [...content.programs];
    programs[i] = { ...programs[i], [field]: value };
    setContent({ ...content, programs });
  };
  const updatePoints = (i, text) => updateProgram(i, "points", text.split("\n"));
  const addProgram = () => setContent({ ...content, programs: [...content.programs, { num: String(content.programs.length + 1).padStart(2, "0"), title: "", img: "", points: [] }] });
  const removeProgram = (i) => setContent({ ...content, programs: content.programs.filter((_, idx) => idx !== i) });

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleaned = { ...content, programs: content.programs.map((p) => ({ ...p, points: p.points.map((pt) => pt.trim()).filter(Boolean) })) };
      await api.put("/admin/content/programs", cleaned);
      setContent(cleaned);
      toast.success("Programs page updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="programs-content-manager" className="bg-background border border-border p-6 space-y-8">
      <div className="space-y-4">
        <div><Label>Eyebrow</Label><Input data-testid="programs-form-eyebrow-input" className="rounded-none mt-1.5" value={content.eyebrow} onChange={(e) => setContent({ ...content, eyebrow: e.target.value })} /></div>
        <div><Label>Title</Label><Input data-testid="programs-form-title-input" className="rounded-none mt-1.5" value={content.title} onChange={(e) => setContent({ ...content, title: e.target.value })} /></div>
        <div><Label>Subtitle</Label><Textarea data-testid="programs-form-subtitle-input" className="rounded-none mt-1.5" value={content.subtitle} onChange={(e) => setContent({ ...content, subtitle: e.target.value })} /></div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-heading text-lg">Program Blocks</h4>
          <Button size="sm" variant="outline" className="rounded-none" data-testid="programs-add-button" onClick={addProgram}><Plus className="h-4 w-4 mr-1" /> Add Program</Button>
        </div>
        <div className="space-y-4">
          {content.programs.map((p, i) => (
            <div key={i} data-testid={`programs-row-${i}`} className="border border-border p-4 space-y-2">
              <div className="flex gap-2">
                <Input data-testid={`programs-num-${i}`} className="rounded-none w-20" value={p.num} onChange={(e) => updateProgram(i, "num", e.target.value)} />
                <Input data-testid={`programs-title-${i}`} className="rounded-none flex-1" value={p.title} onChange={(e) => updateProgram(i, "title", e.target.value)} />
                <Button size="icon" variant="outline" className="rounded-none shrink-0" data-testid={`programs-delete-${i}`} onClick={() => removeProgram(i)}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <Label className="text-xs text-muted-foreground">Image</Label>
              <ImageUploadField testId={`programs-img-${i}`} value={p.img} onChange={(v) => updateProgram(i, "img", v)} />
              <Label className="text-xs text-muted-foreground">Points (one per line)</Label>
              <Textarea data-testid={`programs-points-${i}`} rows={4} className="rounded-none" value={p.points.join("\n")} onChange={(e) => updatePoints(i, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} data-testid="programs-content-save-button" className="w-full rounded-none bg-accent text-accent-foreground">
        {saving ? "Saving…" : "Save Programs Page"}
      </Button>
    </div>
  );
}
